import { useEffect, useState, type FormEvent } from "react";
import {
  ADVANCEMENTS,
  DEATH_RULES,
  MAX_OPPONENTS,
  CHAOS_LEVELS,
  MAX_PREMISE,
  POLICIES,
  SETTINGS,
  type Setting,
  type CharacterSummary,
  GAME_KEPT_DAYS,
  type AdventureChoice,
  type ChaosLevel,
  type GameSummary,
  type RemovedGame,
  type LibraryHit,
  type NewGame,
} from "@ai-rpg/protocol";
import { createGame, gatherTable, listCharacters, listGames, removeGame, restoreGame, searchLibrary } from "../api.ts";
import { CHARACTERS, GUIDE, NEW_CHARACTER, characterPath, tablePath } from "../route.ts";
import { Making } from "./Making.tsx";
import { AdventurePicker } from "./AdventurePicker.tsx";
import { CreatureGrid } from "./CreatureGrid.tsx";
import { LOADING } from "../words.ts";

/**
 * What each content setting is called. Everyone here is an adult, so they are named for how the story is told
 * and not for who it is fit for (they were Family, Teen and Mature until 2026-10-05; the settings are the same).
 */
const POLICY_NAMES: Record<NewGame["policy"], string> = { family: "Gentle", teen: "Adventure", mature: "Mature" };
const POLICY_NOTES: Record<NewGame["policy"], string> = {
  family: "No gore, nobody killed, nothing frightening for long.",
  teen: "Adventure-film violence. The default.",
  mature: "Darker themes, described plainly.",
};

const SETTING_LABELS: Record<Setting, string> = {
  campaign: "A new campaign",
  published: "A published adventure",
  "feast-hall": "The feast hall",
};
/** A line for each on the list of what to play; the whole of it is said beside its options. */
const SETTING_BLURBS: Record<Setting, string> = {
  campaign: "A small world written for your character.",
  published: "An adventure from one of your books.",
  "feast-hall": "One room and a fight, for trying things out.",
};
const SETTING_NOTES: Record<Setting, string> = {
  campaign:
    "A small world is written for your character: places to go, people to talk to, and something to get to the bottom of. Writing it takes about a minute.",
  published:
    "An adventure from one of your books, played as it is written: its places, its people and its story, a part at a time.",
  "feast-hall": "One room with something to fight in it. For trying things out.",
};

const CHAOS_NOTE =
  "A world drawn at random and run by a DM who is out of their mind: a deadly, absurd game with you as a contestant. You don't get to ask for anything; you only say how hard it is. Writing it takes about a minute.";
const CHAOS_LABELS: Record<ChaosLevel, string> = { easy: "Easy", hard: "Hard", brutal: "Brutal" };
const CHAOS_NOTES: Record<ChaosLevel, string> = {
  easy: "Still a challenge, but a fair one: fights that can be lost, ways round for the careful, and help for those who look.",
  hard: "Most places hold a fight worth fearing. Help is scarce and has a price.",
  brutal: "Every place wants you dead, and nothing is free. Expect to run.",
};

type Death = (typeof DEATH_RULES)[number];
const DEATH_LABELS: Record<Death, string> = { on: "By the rules", off: "Never" };
type Advancement = (typeof ADVANCEMENTS)[number];
const ADVANCEMENT_LABELS: Record<Advancement, string> = { milestone: "Milestones", experience: "Experience" };
const ADVANCEMENT_NOTES: Record<Advancement, string> = {
  milestone: "A level for each turning point of the story the party reaches.",
  experience:
    "Experience points for every enemy overcome and every turning point reached; a level when there are enough.",
};
const DEATH_NOTES: Record<Death, string> = {
  on: "Three failed death saves kill a character, yours or a companion.",
  off: "A character who would die is left unconscious and stable.",
};

const dateOf = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** "in 12 days", "tomorrow", "today": how long a deleted game has left. */
function daysLeft(iso: string): string {
  const days = Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000);
  return days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
}

const problemOf = (err: unknown) => (err instanceof Error ? err.message : String(err));

interface Props {
  /** A saved character to start with, when the player came here from its sheet. */
  readonly pick: string | null;
  readonly go: (to: string) => void;
  readonly onOpen: (gameId: string) => void;
}

/** The Play tab: start a game with a saved character, or go back to one in progress. */
export function Lobby({ pick, go, onOpen }: Props) {
  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [removed, setRemoved] = useState<RemovedGame[]>([]);
  /** The game the player has asked to delete, until they say yes or no. */
  const [deleting, setDeleting] = useState<string | null>(null);
  const [characters, setCharacters] = useState<CharacterSummary[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [gamesProblem, setGamesProblem] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(pick);
  const [policy, setPolicy] = useState<NewGame["policy"]>("teen");
  // Until the player says otherwise, a family game has no character death and the others play it as written.
  const [deathChoice, setDeathChoice] = useState<Death | null>(null);
  const death: Death = deathChoice ?? (policy === "family" ? "off" : "on");
  const [advancement, setAdvancement] = useState<Advancement>("milestone");
  const [starting, setStarting] = useState(false);
  const [setting, setSetting] = useState<Setting>("campaign");
  const [together, setTogether] = useState(false);
  const [premise, setPremise] = useState("");
  // The world a campaign is set in: one this character has travelled in already, or (null) a world of its own.
  const [world, setWorld] = useState<string | null>(null);
  // A Chaos game is a campaign nobody asks anything of: only how hard it is.
  const [chaos, setChaos] = useState<ChaosLevel | null>(null);
  const [adventure, setAdventure] = useState<AdventureChoice | null>(null);
  const [library, setLibrary] = useState(false);
  const [opponents, setOpponents] = useState<LibraryHit[]>([]);
  // Offline characters are sheets for a real table: they aren't offered here.
  const playable = characters?.filter((c) => c.use === "table") ?? null;
  const who = playable?.find((c) => c.id === chosen) ?? playable?.[0] ?? null;
  const edition = who?.edition ?? "2024";
  // A character plays one story at a time: with one under way, only the feast hall is open to it.
  const held = !!who?.story && setting !== "feast-hall";

  const loadGames = () =>
    listGames()
      .then((r) => {
        setGames(r.games);
        setRemoved(r.removed);
        setGamesProblem(null);
      })
      .catch((err: unknown) => setGamesProblem(problemOf(err)));
  /** Delete a game, or bring one back, and show the lists as they then stand. */
  const change = (work: Promise<void>) => {
    setDeleting(null);
    void work.then(loadGames).catch((err: unknown) => setGamesProblem(problemOf(err)));
  };

  useEffect(() => {
    void loadGames();
    listCharacters()
      .then(setCharacters)
      .catch((err: unknown) => setProblem(problemOf(err)));
    // The picker only appears where there are books to pick from.
    searchLibrary("creature")
      .then((r) => setLibrary(r.available))
      .catch(() => setLibrary(false));
  }, []);

  // The two editions print different versions of the same creatures, so earlier picks don't carry over.
  const choose = (next: CharacterSummary) => {
    if (next.edition !== edition) setOpponents([]);
    setChosen(next.id);
  };

  const start = async (e: FormEvent) => {
    e.preventDefault();
    if (!who || starting) return;
    setStarting(true);
    setProblem(null);
    try {
      const game = {
        characterId: who.id,
        policy,
        characterDeath: death,
        advancement,
        setting,
        premise: setting === "campaign" && !chaos && premise.trim() ? premise.trim() : null,
        chaos: setting === "campaign" ? chaos : null,
        world: setting === "campaign" && !chaos && who.worlds?.some((w) => w.id === world) ? world : null,
        adventure: setting === "published" ? (adventure?.bookId ?? null) : null,
        opponents: setting === "feast-hall" ? opponents.map((o) => o.key) : [],
      };
      // With others: the table gathers first, and the world is written while they sit down.
      if (together && setting !== "feast-hall") go(tablePath((await gatherTable(game)).id));
      else onOpen(await createGame(game));
    } catch (err) {
      setProblem(problemOf(err));
      setStarting(false);
    }
  };

  return (
    <main className="lobby">
      <form className="lobby-form" onSubmit={start}>
        <section className="lobby-mode" aria-label="What to play">
          <h1 className="lobby-title">A new game</h1>
          <p className="lobby-lede">
            Say what your character does, in your own words. The rules roll the dice and keep the record; the story is
            told from what really happened.
          </p>
          {/* Someone with nothing begun yet is shown the way in, once: it goes when there is a game. */}
          {games?.length === 0 && removed.length === 0 && playable !== null && (
            <ol className="first-steps" aria-label="How to begin">
              <li data-done={playable.length > 0}>
                <span className="first-step-word">Make a character</span>
                {playable.length > 0 ? (
                  "Done."
                ) : (
                  <a
                    className="link"
                    href={CHARACTERS}
                    onClick={(e) => {
                      e.preventDefault();
                      go(CHARACTERS);
                    }}
                  >
                    On the Characters tab
                  </a>
                )}
              </li>
              <li>
                <span className="first-step-word">Choose what to play</span>
                The Feast Hall is one room for trying things out; a campaign is a story written for you.
              </li>
              <li>
                <span className="first-step-word">Say what you do</span>
                In your own words. The{" "}
                <a
                  className="link"
                  href={GUIDE}
                  onClick={(e) => {
                    e.preventDefault();
                    go(GUIDE);
                  }}
                >
                  Guide
                </a>{" "}
                has the rest.
              </li>
            </ol>
          )}

          <fieldset className="field">
            <legend className="field-label">Character</legend>
            {playable === null && !problem && (
              <p className="quiet" role="status">
                {LOADING}
              </p>
            )}
            {playable?.length === 0 && (
              <p className="field-note">
                You have no characters to play yet. Make one on the Characters tab, and it will be here for every game
                you start.
              </p>
            )}
            {playable && playable.length > 0 && (
              <div className="choices">
                {playable.map((c) => (
                  <label key={c.id} className="choice" data-on={who?.id === c.id}>
                    <input type="radio" name="character" checked={who?.id === c.id} onChange={() => choose(c)} />
                    <span className="choice-name">{c.name}</span>
                    <span className="choice-note">
                      {c.summary} · {c.edition} rules
                    </span>
                  </label>
                ))}
              </div>
            )}
            <p className="field-note">
              <button type="button" className="link" onClick={() => go(NEW_CHARACTER)}>
                New character
              </button>
              {who && (
                <>
                  {" · "}
                  <button type="button" className="link" onClick={() => go(characterPath(who.id))}>
                    See {who.name}'s sheet
                  </button>
                </>
              )}
            </p>
          </fieldset>

          <fieldset className="field">
            <legend className="field-label">What to play</legend>
            <div className="choices choices-stacked">
              {SETTINGS.map((s) => (
                <label key={s} className="choice" data-on={setting === s && !(s === "campaign" && chaos)}>
                  <input
                    type="radio"
                    name="setting"
                    checked={setting === s && !(s === "campaign" && chaos)}
                    onChange={() => {
                      setSetting(s);
                      setChaos(null);
                    }}
                  />
                  <span className="choice-name">{SETTING_LABELS[s]}</span>
                  <span className="choice-note">{SETTING_BLURBS[s]}</span>
                </label>
              ))}
              <label className="choice" data-on={setting === "campaign" && !!chaos}>
                <input
                  type="radio"
                  name="setting"
                  checked={setting === "campaign" && !!chaos}
                  onChange={() => {
                    setSetting("campaign");
                    setChaos("easy");
                  }}
                />
                <span className="choice-name">Chaos</span>
                <span className="choice-note">A deadly game show. You only say how hard.</span>
              </label>
            </div>
          </fieldset>
        </section>

        <section className="lobby-options" aria-label="How to play it">
          <h2 className="lobby-mode-name">{setting === "campaign" && chaos ? "Chaos" : SETTING_LABELS[setting]}</h2>
          <p className="field-note">{setting === "campaign" && chaos ? CHAOS_NOTE : SETTING_NOTES[setting]}</p>

          {setting === "campaign" && chaos && (
            <fieldset className="field">
              <legend className="field-label">How hard</legend>
              <div className="segments">
                {CHAOS_LEVELS.map((l) => (
                  <label key={l} className="segment" data-on={chaos === l} title={CHAOS_NOTES[l]}>
                    <input type="radio" name="chaos" checked={chaos === l} onChange={() => setChaos(l)} />
                    {CHAOS_LABELS[l]}
                  </label>
                ))}
              </div>
              <p className="field-note">{CHAOS_NOTES[chaos]}</p>
            </fieldset>
          )}

          {setting === "campaign" && !chaos && (
            <label className="field">
              <span className="field-label">What should it be about?</span>
              <textarea
                className="input"
                rows={3}
                maxLength={MAX_PREMISE}
                value={premise}
                placeholder="A drowned bell that still rings. A debt my character owes in a river town."
                onChange={(e) => setPremise(e.target.value)}
              />
              <span className="field-note">Leave it empty to be surprised.</span>
            </label>
          )}

          {setting === "campaign" && !chaos && who?.worlds && who.worlds.length > 0 && (
            <fieldset className="field">
              <legend className="field-label">Which world</legend>
              <div className="segments">
                <label className="segment" data-on={world === null}>
                  <input type="radio" name="world" checked={world === null} onChange={() => setWorld(null)} />A new
                  world
                </label>
                {who.worlds.map((w) => (
                  <label key={w.id} className="segment" data-on={world === w.id}>
                    <input type="radio" name="world" checked={world === w.id} onChange={() => setWorld(w.id)} />
                    {w.name}
                  </label>
                ))}
              </div>
              <p className="field-note">
                {world === null
                  ? "Somewhere nobody has been: it starts a map of its own."
                  : `Another country of the world ${who.name} has travelled in: what is charted there is added to the same map.`}
              </p>
            </fieldset>
          )}

          {setting === "published" && who && (
            <AdventurePicker level={who.level} edition={edition} chosen={adventure} onChoose={setAdventure} />
          )}

          <fieldset className="field">
            <legend className="field-label">Content</legend>
            <div className="segments">
              {POLICIES.map((p) => (
                <label key={p} className="segment" data-on={policy === p} title={POLICY_NOTES[p]}>
                  <input type="radio" name="policy" checked={policy === p} onChange={() => setPolicy(p)} />
                  {POLICY_NAMES[p]}
                </label>
              ))}
            </div>
            <p className="field-note">{POLICY_NOTES[policy]}</p>
          </fieldset>

          <fieldset className="field">
            <legend className="field-label">Character death</legend>
            <div className="segments">
              {DEATH_RULES.map((d) => (
                <label key={d} className="segment" data-on={death === d} title={DEATH_NOTES[d]}>
                  <input type="radio" name="death" checked={death === d} onChange={() => setDeathChoice(d)} />
                  {DEATH_LABELS[d]}
                </label>
              ))}
            </div>
            <p className="field-note">{DEATH_NOTES[death]}</p>
          </fieldset>

          {setting !== "feast-hall" && (
            <fieldset className="field">
              <legend className="field-label">Players</legend>
              <div className="segments">
                <label className="segment" data-on={!together}>
                  <input type="radio" name="players" checked={!together} onChange={() => setTogether(false)} />
                  Just me
                </label>
                <label className="segment" data-on={together}>
                  <input type="radio" name="players" checked={together} onChange={() => setTogether(true)} />
                  With others
                </label>
              </div>
              <p className="field-note">
                {together
                  ? "You get a link to send. Everyone waits at the table while the world is written, and you begin when they are here."
                  : "You and three companions the DM plays. Others can still be asked in later, from the game."}
              </p>
            </fieldset>
          )}

          {setting !== "feast-hall" && (
            <fieldset className="field">
              <legend className="field-label">Levelling</legend>
              <div className="segments">
                {ADVANCEMENTS.map((a) => (
                  <label key={a} className="segment" data-on={advancement === a} title={ADVANCEMENT_NOTES[a]}>
                    <input
                      type="radio"
                      name="advancement"
                      checked={advancement === a}
                      onChange={() => setAdvancement(a)}
                    />
                    {ADVANCEMENT_LABELS[a]}
                  </label>
                ))}
              </div>
              <p className="field-note">{ADVANCEMENT_NOTES[advancement]}</p>
            </fieldset>
          )}

          {library && who && setting === "feast-hall" && (
            <CreatureGrid
              label="Opponents"
              note="Nobody chosen: an ogre and two goblins, as usual."
              max={MAX_OPPONENTS}
              edition={edition}
              chosen={opponents}
              onChange={setOpponents}
            />
          )}

          <button
            className="button button-primary"
            type="submit"
            disabled={!who || starting || (setting === "published" && !adventure) || held}
          >
            {together && setting !== "feast-hall"
              ? starting
                ? "Gathering the table…"
                : `Gather a table${who ? ` as ${who.name}` : ""}`
              : starting
                ? setting === "campaign"
                  ? "Writing the world…"
                  : setting === "published"
                    ? "Opening the book…"
                    : "Setting the scene…"
                : setting === "campaign"
                  ? `${chaos ? "Start a Chaos game" : "Start a campaign"}${who ? ` as ${who.name}` : ""}`
                  : setting === "published"
                    ? `Start ${adventure?.title ?? "an adventure"}${who ? ` as ${who.name}` : ""}`
                    : `Start at the Feast Hall${who ? ` as ${who.name}` : ""}`}
          </button>
          {starting && setting !== "feast-hall" && !together && (
            <Making
              usual={
                setting === "campaign"
                  ? "It usually takes about a minute."
                  : "A minute if this adventure has been played here before, two or three if not."
              }
            />
          )}
          {held && who?.story && (
            <p className="field-note">
              {who.name} is in{" "}
              <button type="button" className="link" onClick={() => onOpen(who.story!.id)}>
                {who.story.title}
              </button>
              . A character plays one story at a time: finish that one, or withdraw {who.name} from it on{" "}
              <button type="button" className="link" onClick={() => go(characterPath(who.id))}>
                their sheet
              </button>
              . The feast hall is still open.
            </p>
          )}
          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}
        </section>
      </form>

      <section className="lobby-games" aria-label="Games in progress">
        <h2 className="section-label">In progress</h2>
        {games === null && !gamesProblem && (
          <p className="quiet" role="status">
            {LOADING}
          </p>
        )}
        {gamesProblem && (
          <p className="problem" role="alert">
            {gamesProblem}
          </p>
        )}
        {games?.length === 0 && <p className="quiet">Nothing yet. Your games are kept here once you start one.</p>}
        <ul className="game-list game-frame">
          {games?.map((g) => (
            <li key={g.id} className="game-item">
              <button className="game-row" onClick={() => onOpen(g.id)}>
                <span className="game-who">{g.character}</span>
                <span className="game-where">{g.title}</span>
                <span className="game-when">{dateOf(g.createdAt)}</span>
              </button>
              <button
                className="link game-delete"
                aria-label={`Delete ${g.character}'s game, ${g.title}`}
                onClick={() => setDeleting(deleting === g.id ? null : g.id)}
              >
                Delete
              </button>
              {deleting === g.id && (
                <p className="confirm game-confirm" role="alert">
                  Delete this game? It is kept for {GAME_KEPT_DAYS} days, then erased.
                  <button className="button button-confirm" onClick={() => change(removeGame(g.id))}>
                    Delete
                  </button>
                  <button className="link" onClick={() => setDeleting(null)}>
                    Keep
                  </button>
                </p>
              )}
            </li>
          ))}
        </ul>
        {removed.length > 0 && (
          <details className="game-removed">
            <summary className="link">Deleted ({removed.length})</summary>
            <ul className="game-list game-frame">
              {removed.map((g) => (
                <li key={g.id} className="game-item">
                  <span className="game-row game-row-gone">
                    <span className="game-who">{g.character}</span>
                    <span className="game-where">
                      {g.title} · erased {daysLeft(g.purgeAt)}
                    </span>
                  </span>
                  <button className="link game-delete" onClick={() => change(restoreGame(g.id))}>
                    Bring back
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </main>
  );
}
